import {useCallback, useEffect, useState} from 'react'

import {useMatrixContext} from '#/state/matrix'
import {useDirectRooms} from '#/state/matrix/hooks/useDirectRooms'
import {type MatrixRoomSummary, roomToSummary} from '#/state/matrix/utils'

export type {MatrixRoomSummary}

export function useMatrixRooms() {
  const {client, isConnected} = useMatrixContext()
  const {isDirectRoom, getDirectRoomUserId} = useDirectRooms()
  const [rooms, setRooms] = useState<MatrixRoomSummary[]>([])

  const refreshRooms = useCallback(() => {
    if (!client) {
      setRooms([])
      return
    }
    const myUserId = client.getUserId() || ''
    const matrixRooms = client.getRooms()
    const summaries = matrixRooms
      .filter(room => room.getMyMembership() === 'join')
      .map(room =>
        roomToSummary(room, myUserId, isDirectRoom, getDirectRoomUserId),
      )
      .sort((a, b) => (b.lastMessageTs || 0) - (a.lastMessageTs || 0))
    setRooms(summaries)
  }, [client, isDirectRoom, getDirectRoomUserId])

  useEffect(() => {
    if (!client || !isConnected) return

    refreshRooms()

    const onEvent = () => refreshRooms()
    client.on('Room.timeline' as any, onEvent)
    client.on('Room' as any, onEvent)
    client.on('Room.receipt' as any, onEvent)

    const onMembership = () => {
      refreshRooms()
    }
    client.on('Room.myMembership' as any, onMembership)

    return () => {
      client.removeListener('Room.timeline' as any, onEvent)
      client.removeListener('Room' as any, onEvent)
      client.removeListener('Room.receipt' as any, onEvent)
      client.removeListener('Room.myMembership' as any, onMembership)
    }
  }, [client, isConnected, refreshRooms])

  return {rooms, refreshRooms, isConnected}
}
